/**
 * Crisp Configuration Utility
 *
 * Centralized configuration for Crisp chat widget
 * Uses environment variables for security and flexibility
 * Supports both web (crisp-sdk-web) and native mobile (Capacitor Crisp plugin)
 */

import { Capacitor } from "@capacitor/core";
import { Crisp } from "crisp-sdk-web";
import { createLogger } from "./logger";

const logger = createLogger("CrispConfig");

// Default Crisp configuration options
const DEFAULT_CONFIG = {
  autoload: true,
  lockMaximized: false,
  lockFullview: false
};

/**
 * Check if we're running on a native mobile platform
 * @returns {boolean}
 */
const isNativeMobile = () => {
  const platform = Capacitor.getPlatform();
  return platform === "ios" || platform === "android";
};

/**
 * Configure Crisp chat widget for native mobile (iOS/Android)
 * Uses @capgo/capacitor-crisp plugin
 * @param {Object} customConfig - Custom configuration options
 * @returns {Promise<boolean>} - Whether Crisp was successfully configured
 */
const configureCrispNative = async (customConfig = {}) => {
  try {
    const crispWebsiteId = import.meta.env.VITE_CRISP_WEBSITE_ID;

    if (!crispWebsiteId) {
      logger.warn(
        "⚠️ VITE_CRISP_WEBSITE_ID not found in environment variables. Crisp chat will not be initialized."
      );
      return false;
    }

    // Dynamically import the Capacitor Crisp plugin
    const { CapacitorCrisp } = await import("@capgo/capacitor-crisp");

    // Configure the plugin with the website ID
    await CapacitorCrisp.configure({ websiteID: crispWebsiteId });

    logger.log("✅ Crisp native plugin configured successfully");
    return true;
  } catch (error) {
    logger.error("❌ Failed to configure Crisp native plugin:", error);
    return false;
  }
};

/**
 * Configure Crisp chat widget for web
 * Uses crisp-sdk-web
 * @param {Object} customConfig - Custom configuration options
 * @returns {boolean} - Whether Crisp was successfully configured
 */
const configureCrispWeb = (customConfig = {}) => {
  try {
    // Get Crisp website ID from environment variables
    const crispWebsiteId = import.meta.env.VITE_CRISP_WEBSITE_ID;

    if (!crispWebsiteId) {
      logger.warn(
        "⚠️ VITE_CRISP_WEBSITE_ID not found in environment variables. Crisp chat will not be initialized."
      );
      return false;
    }

    // Merge default config with custom config
    const config = {
      ...DEFAULT_CONFIG,
      ...customConfig
    };

    // Configure Crisp
    Crisp.configure(crispWebsiteId, config);

    logger.log("✅ Crisp web SDK configured successfully");
    return true;
  } catch (error) {
    logger.error("❌ Failed to configure Crisp web SDK:", error);
    return false;
  }
};

/**
 * Configure Crisp chat widget
 * Automatically detects platform and uses appropriate implementation
 * @param {Object} customConfig - Custom configuration options to override defaults
 * @returns {Promise<boolean>} - Whether Crisp was successfully configured
 */
export const configureCrisp = async (customConfig = {}) => {
  if (isNativeMobile()) {
    return await configureCrispNative(customConfig);
  } else {
    return configureCrispWeb(customConfig);
  }
};

/**
 * Configure Crisp with locked interface (for support pages)
 * Only applicable for web platform (native plugin doesn't support these options)
 * @param {Object} customConfig - Custom configuration options
 * @returns {Promise<boolean>} - Whether Crisp was successfully configured
 */
export const configureCrispLocked = async (customConfig = {}) => {
  // For native mobile, use native configuration directly
  // Lock options are not supported by the native plugin
  if (isNativeMobile()) {
    return await configureCrispNative(customConfig);
  }
  
  // For web, use locked configuration
  return configureCrispWeb({
    lockMaximized: true,
    lockFullview: true,
    ...customConfig
  });
};

/**
 * Check if Crisp is properly configured
 * @returns {boolean} - Whether Crisp is configured and ready
 */
export const isCrispConfigured = () => {
  try {
    // Check if Crisp is available and configured
    return (
      typeof window !== "undefined" &&
      window.Crisp &&
      window.Crisp.is("chat:visible") !== undefined
    );
  } catch (_error) {
    return false;
  }
};

/**
 * Get current Crisp configuration status
 * @returns {Object} - Configuration status information
 */
export const getCrispStatus = () => {
  return {
    configured: isCrispConfigured(),
    websiteId: import.meta.env.VITE_CRISP_WEBSITE_ID || null,
    environment: process.env.NODE_ENV || "development"
  };
};
